import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Minus, Plus, Trash2, Tag, ShoppingBag, ArrowLeft } from "lucide-react";

const TIP_OPTIONS = [0, 2, 3, 5];

export default function CartPage() {
  const navigate = useNavigate();
  const { cart, updateQty, removeItem, clear } = useCart();
  const [promo, setPromo] = useState("");
  const [tip, setTip] = useState(3);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);

  const buildPayload = () => ({
    restaurant_id: cart.restaurant?.restaurant_id,
    items: cart.items.map((i) => ({ menu_item_id: i.item_id, quantity: i.quantity, notes: i.notes || "" })),
    tip,
    promo_code: promo || null,
    address: "",
    phone: "",
    payment_method: "card",
  });

  useEffect(() => {
    const compute = async () => {
      if (!cart.restaurant || !cart.items.length) { setQuote(null); return; }
      setLoading(true);
      try {
        const { data } = await api.post("/orders/quote", buildPayload());
        setQuote(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    };
    compute();
    // eslint-disable-next-line
  }, [cart, tip, promo]);

  if (!cart.restaurant || !cart.items.length) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <div className="mx-auto w-20 h-20 rounded-3xl bg-eatup-green/10 flex items-center justify-center">
          <ShoppingBag className="w-10 h-10 text-eatup-green" />
        </div>
        <h1 className="font-display text-3xl font-bold mt-6">Your cart is empty</h1>
        <p className="text-neutral-500 mt-2">Pick a restaurant and start adding delicious food.</p>
        <button data-testid="browse-restaurants-btn" onClick={() => navigate("/home")} className="btn-primary mt-6">Browse restaurants</button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 sm:py-10 pb-32">
      <button onClick={() => navigate(-1)} data-testid="cart-back-btn" className="flex items-center gap-2 text-neutral-600 hover:text-eatup-charcoal">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h1 className="font-display text-4xl font-bold mt-4">Your cart</h1>
      <p className="text-neutral-500 mt-1">From <span className="text-eatup-charcoal font-semibold">{cart.restaurant.name}</span></p>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {cart.items.map((i) => (
            <div key={i.item_id} data-testid={`cart-line-${i.item_id}`} className="bg-white rounded-2xl card-shadow p-4 flex items-center gap-4">
              <img src={i.image_url} alt={i.name} className="w-20 h-20 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold">{i.name}</div>
                <div className="text-sm text-neutral-500">${i.price.toFixed(2)}</div>
              </div>
              <div className="flex items-center gap-2 bg-eatup-gray rounded-full px-1 py-1">
                <button data-testid={`decr-${i.item_id}`} onClick={() => updateQty(i.item_id, i.quantity - 1)} className="w-8 h-8 rounded-full bg-white flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                <span className="w-6 text-center font-bold tabular">{i.quantity}</span>
                <button data-testid={`incr-${i.item_id}`} onClick={() => updateQty(i.item_id, i.quantity + 1)} className="w-8 h-8 rounded-full bg-white flex items-center justify-center"><Plus className="w-4 h-4" /></button>
              </div>
              <button data-testid={`remove-${i.item_id}`} onClick={() => removeItem(i.item_id)} className="p-2 text-neutral-400 hover:text-red-500 transition"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}

          {/* Promo */}
          <div className="bg-white rounded-2xl card-shadow p-4 flex items-center gap-3">
            <Tag className="w-5 h-5 text-eatup-green" />
            <input
              data-testid="promo-input"
              value={promo}
              onChange={(e) => setPromo(e.target.value.toUpperCase())}
              placeholder="Promo code — try EATSGND10"
              className="flex-1 outline-none font-mono tracking-widest text-sm"
            />
            {quote?.promo_description && (
              <span className="text-xs bg-eatup-green/10 text-eatup-green font-bold px-2 py-1 rounded-full">{quote.promo_description}</span>
            )}
          </div>

          {/* Tip */}
          <div className="bg-white rounded-2xl card-shadow p-5">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Tip the driver</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {TIP_OPTIONS.map((t) => (
                <button
                  key={t}
                  data-testid={`tip-${t}`}
                  onClick={() => setTip(t)}
                  className={`px-4 py-2 rounded-full border font-semibold text-sm transition ${
                    tip === t ? "bg-eatup-green text-white border-eatup-green" : "bg-white border-neutral-200 hover:border-neutral-300"
                  }`}
                >
                  {t === 0 ? "No tip" : `$${t}`}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Summary */}
        <aside className="lg:sticky lg:top-24 h-fit bg-white rounded-3xl card-shadow p-6">
          <h3 className="font-display font-bold text-xl">Order summary</h3>
          {loading || !quote ? (
            <div className="mt-4 space-y-3">
              <div className="skeleton h-4 w-full" />
              <div className="skeleton h-4 w-2/3" />
            </div>
          ) : (
            <div className="mt-4 space-y-2 text-sm">
              <Row label="Subtotal" value={`EC$${quote.subtotal.toFixed(2)}`} />
              <Row
                label={quote.distance_km ? `Delivery fee (${quote.distance_km.toFixed(1)} km)` : "Delivery fee"}
                value={`EC$${quote.delivery_fee.toFixed(2)}`}
              />
              <Row label="Platform fee" value={`EC$${(quote.platform_flat_fee ?? quote.service_charge).toFixed(2)}`} />
              {quote.small_order_fee > 0 && (
                <Row label="Small-order fee" value={`EC$${quote.small_order_fee.toFixed(2)}`} />
              )}
              <Row label="Tax" value={`EC$${quote.tax.toFixed(2)}`} />
              <Row label="Driver tip" value={`EC$${quote.tip.toFixed(2)}`} />
              {quote.discount > 0 && (
                <Row label={`Promo (${quote.promo_code})`} value={`- EC$${quote.discount.toFixed(2)}`} highlight />
              )}
              <div className="h-px bg-neutral-100 my-2" />
              <div className="flex items-center justify-between font-display font-bold text-lg">
                <span>Total</span>
                <span data-testid="cart-total">EC${quote.total.toFixed(2)}</span>
              </div>
            </div>
          )}
          <button
            data-testid="go-to-checkout-btn"
            onClick={() => navigate("/checkout", { state: { tip, promo } })}
            className="w-full btn-primary mt-6"
          >
            Checkout
          </button>
          <button
            data-testid="clear-cart-btn"
            onClick={() => { clear(); toast("Cart cleared"); }}
            className="w-full mt-2 text-xs text-neutral-500 hover:text-red-500 py-2"
          >
            Clear cart
          </button>
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-neutral-500">{label}</span>
      <span className={`font-semibold tabular ${highlight ? "text-eatup-green" : ""}`}>{value}</span>
    </div>
  );
}
